import React, { useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { naira, fmtDateTime } from "../../lib/format";
import StatusBadge from "../StatusBadge";
import { PackageCheck, Ban } from "lucide-react";

const BURG = { background: "#8B1E1E" };

export default function BranchOrderDetail({ order, onClose, onChanged, canReceive }) {
  const [busy, setBusy] = useState(false);

  const receive = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/procurement/branch-orders/${order.id}/receive`, {});
      if (r.duplicate) toast.info("This receipt was already posted.");
      else toast.success(`${r.grn?.number} posted — stock updated`);
      onChanged(); onClose();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel this branch order?")) return;
    setBusy(true);
    try {
      await api.post(`/procurement/branch-orders/${order.id}/cancel`, {});
      toast.success("Cancelled"); onChanged(); onClose();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const req = order.approval_request;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="bo-detail">
        <DialogHeader>
          <DialogTitle className="font-brand flex items-center gap-3" style={{ color: "#8B1E1E" }}>
            {order.number} <StatusBadge status={order.status} />
          </DialogTitle>
          <DialogDescription>Branch order — {order.supplier_name} · {order.location_name || "—"}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><div className="text-xs" style={{ color: "#a58f7c" }}>Requested by</div><div className="font-medium">{order.requested_by_name || "—"}</div></div>
            <div><div className="text-xs" style={{ color: "#a58f7c" }}>Raised</div><div className="font-medium">{fmtDateTime(order.created_at)}</div></div>
            {order.approved_by_name && <div><div className="text-xs" style={{ color: "#a58f7c" }}>Approved by</div><div className="font-medium">{order.approved_by_name}</div></div>}
            {order.grn_number && <div><div className="text-xs" style={{ color: "#a58f7c" }}>GRN</div><div className="font-medium" style={{ color: "#8B1E1E" }}>{order.grn_number}</div></div>}
          </div>
          {order.reason && <div className="text-sm p-3 rounded-xl" style={{ background: "#FFF9EE", color: "#3B2722" }}>{order.reason}</div>}

          <div className="rounded-2xl border" style={{ borderColor: "#E9DED0" }}>
            <div className="grid grid-cols-[1fr_80px_110px_110px] text-[11px] uppercase tracking-widest font-semibold px-3 py-2 border-b" style={{ color: "#765A4A", borderColor: "#E9DED0" }}>
              <div>Item</div><div className="text-right">Qty</div><div className="text-right">Unit</div><div className="text-right">Total</div>
            </div>
            {order.items.map((l, i) => (
              <div key={i} className="grid grid-cols-[1fr_80px_110px_110px] px-3 py-2 text-sm" style={{ color: "#3B2722" }}>
                <div>{l.name}</div>
                <div className="text-right">{l.quantity}</div>
                <div className="text-right">{naira(l.unit_price)}</div>
                <div className="text-right font-semibold">{naira(l.total)}</div>
              </div>
            ))}
            <div className="grid grid-cols-[1fr_80px_110px_110px] px-3 py-2 text-sm border-t font-semibold" style={{ background: "#FFF9EE", borderColor: "#E9DED0" }}>
              <div className="col-span-3 text-right">Total</div>
              <div className="text-right" style={{ color: "#8B1E1E" }}>{naira(order.total)}</div>
            </div>
          </div>

          {req && (
            <div className="rounded-2xl border p-3" style={{ background: "#FFFDF9", borderColor: "#E9DED0" }} data-testid="bo-approval-trail">
              <div className="text-[11px] uppercase tracking-widest font-semibold mb-2" style={{ color: "#765A4A" }}>Approval Chain — {req.workflow_type}</div>
              <div className="text-xs mb-1">Status: <b style={{ color: req.status === "APPROVED" ? "#4F7A52" : req.status === "REJECTED" ? "#A52222" : "#B7791F" }}>{req.status}</b></div>
              {(req.approvals || []).length > 0 && (
                <ul className="space-y-1 text-xs">
                  {req.approvals.map((a, i) => (
                    <li key={i} style={{ color: "#3B2722" }}>
                      <b>{a.symbol}</b> · {a.action} by {a.actor_name} · {fmtDateTime(a.at)}
                      {a.reason && <span> — {a.reason}</span>}
                    </li>
                  ))}
                </ul>
              )}
              {req.rejection_reason && <div className="text-xs mt-1" style={{ color: "#A52222" }}>Reason: {req.rejection_reason}</div>}
            </div>
          )}
        </div>

        <DialogFooter>
          {order.status === "pending_approval" && <Button variant="outline" onClick={cancel} disabled={busy} data-testid="bo-cancel-order"><Ban size={14} className="mr-1" /> Cancel Order</Button>}
          <Button variant="outline" onClick={onClose} data-testid="bo-close">Close</Button>
          {canReceive && order.status === "approved" && (
            <Button onClick={receive} disabled={busy} style={BURG} className="text-white" data-testid="bo-receive"><PackageCheck size={15} className="mr-1" /> {busy ? "Posting…" : "Receive Stock"}</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
